import React, { useEffect } from 'react'
import { CheckCircle, XCircle, AlertCircle, X, ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

interface ToastProps {
 message: string
 type?: ToastType
 duration?: number
 onClose: () => void
 helpLink?: string
 showMetaBadge?: boolean
}

export const Toast: React.FC<ToastProps> = ({
 message,
 type = 'success',
 duration = 3000,
 onClose,
 helpLink,
 showMetaBadge
}) => {
 const { t } = useTranslation()
 useEffect(() => {
 if (duration > 0) {
 const timer = setTimeout(() => {
 onClose()
 }, duration)

 return () => clearTimeout(timer)
 }
 }, [duration, onClose])

 const getIcon = () => {
 switch (type) {
 case 'success':
 return <CheckCircle className="h-5 w-5 text-green-500" />
 case 'error':
 return <XCircle className="h-5 w-5 text-red-500" />
 case 'warning':
 return <AlertCircle className="h-5 w-5 text-yellow-500" />
 default:
 return <AlertCircle className="h-5 w-5 text-blue-500" />
 }
 }

 const getStyles = () => {
 switch (type) {
 case 'success':
 return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
 case 'error':
 return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
 case 'warning':
 return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
 default:
 return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
 }
 }

 return (
 <div className={`fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 fade-in duration-300`}>
 <div className={`flex flex-col gap-2 px-4 py-3 rounded-lg border shadow-lg max-w-md ${getStyles()}`}>
 <div className="flex items-center gap-3">
 {getIcon()}
 <p className="flex-1 text-sm font-medium text-foreground">
 {message}
 </p>
 <button
 onClick={onClose}
 className="text-muted-foreground hover:text-muted-foreground dark:hover:text-gray-300 transition-colors"
 >
 <X className="h-4 w-4" />
 </button>
 </div>

 {showMetaBadge && (
 <div className="ml-8 text-xs text-muted-foreground opacity-75">
 {t('whatsapp.errorHelp.metaIssue')}
 </div>
 )}

 {helpLink && (
 <a
 href={helpLink}
 target="_blank"
 rel="noopener noreferrer"
 className="ml-8 text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
 >
 {t('whatsapp.errorHelp.openMetaManager')}
 <ExternalLink className="h-3 w-3" />
 </a>
 )}
 </div>
 </div>
 )
}

